/**
 * Post-run generation Trace helpers: parse the worker's Claude stream-json NDJSON
 * (`generation/attempt-0/trace.jsonl`) into narration + friendly action steps for the
 * "How generated" panel. Labels mirror the worker's `_activity_labels` / `_friendly_action`.
 */

export type GenerationTraceStep =
  { kind: 'narration'; text: string } | { kind: 'action'; label: string };

const HIDDEN_TOOLS = new Set(['TodoWrite', 'ToolSearch']);

const STEP_ARG_KEYS = [
  'url',
  'element',
  'text',
  'key',
  'value',
  'name',
  'selector',
  'filename',
  'expression',
  'file_path',
  'path',
  'pattern',
] as const;

const ACTION_VERBS: Record<string, { verb: string; includeArg: boolean }> = {
  browser_navigate: { verb: 'Opened', includeArg: true },
  browser_navigate_back: { verb: 'Went back', includeArg: false },
  browser_click: { verb: 'Clicked', includeArg: true },
  browser_type: { verb: 'Typed into', includeArg: true },
  browser_fill_form: { verb: 'Filled in the form', includeArg: false },
  browser_press_key: { verb: 'Pressed', includeArg: true },
  browser_select_option: { verb: 'Selected an option', includeArg: false },
  browser_hover: { verb: 'Hovered over', includeArg: true },
  browser_drag: { verb: 'Dragged an element', includeArg: false },
  browser_take_screenshot: { verb: 'Captured a screenshot', includeArg: false },
  browser_snapshot: { verb: 'Inspected the page', includeArg: false },
  browser_run_code: { verb: 'Ran a script on the page', includeArg: false },
  browser_evaluate: { verb: 'Ran a script on the page', includeArg: false },
  browser_wait_for: { verb: 'Waited for', includeArg: true },
  browser_file_upload: { verb: 'Uploaded a file', includeArg: false },
  browser_handle_dialog: { verb: 'Handled a dialog', includeArg: false },
  browser_console_messages: {
    verb: 'Checked the browser console',
    includeArg: false,
  },
  browser_network_requests: {
    verb: 'Checked network requests',
    includeArg: false,
  },
  browser_tabs: { verb: 'Switched browser tab', includeArg: false },
  browser_resize: { verb: 'Resized the window', includeArg: false },
  Read: { verb: 'Read', includeArg: true },
  Glob: { verb: 'Searched files', includeArg: false },
  Grep: { verb: 'Searched file contents', includeArg: false },
};

const ACTIVITY_REDACT_KEYS = new Set(['text', 'value']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Strip query/fragment from a URL before showing it in a label (avoids leaking tokens).
 * String-split (not `URL`) so path shape stays unchanged — `URL#toString` can add a trailing `/`.
 */
export function redactUrlForDisplay(url: string): string {
  const queryIndex = url.indexOf('?');
  const hashIndex = url.indexOf('#');
  let end = url.length;
  if (queryIndex >= 0) end = Math.min(end, queryIndex);
  if (hashIndex >= 0) end = Math.min(end, hashIndex);
  const redacted = url.slice(0, end);
  const schemeEnd = redacted.indexOf('://');
  const authorityStart = redacted.startsWith('//')
    ? 2
    : schemeEnd >= 0
      ? schemeEnd + 3
      : -1;
  if (authorityStart < 0) return redacted;
  const pathStart = redacted.indexOf('/', authorityStart);
  const authorityEnd = pathStart < 0 ? redacted.length : pathStart;
  const userinfoEnd = redacted.lastIndexOf('@', authorityEnd - 1);
  if (userinfoEnd < authorityStart) return redacted;

  return `${redacted.slice(0, authorityStart)}${redacted.slice(userinfoEnd + 1)}`;
}

function toolBaseName(name: string): string {
  const parts = name.split('__');
  return parts[parts.length - 1] ?? name;
}

function salientArg(input: unknown): string {
  if (!isRecord(input)) return '';
  for (const key of STEP_ARG_KEYS) {
    const value = input[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim().replace(/\s+/g, ' ');
    }
  }
  return '';
}

/**
 * Plain-language label for one tool_use block ("Opened https://…", "Read spec.pdf").
 */
export function friendlyActionLabel(name: string, input: unknown): string {
  const baseName = toolBaseName(name);
  let argument = salientArg(input);
  if (baseName === 'Read') {
    argument = argument.split('/').pop() ?? argument;
  }
  if (argument.length > 160) {
    argument = `${argument.slice(0, 157)}…`;
  }
  const mapped = ACTION_VERBS[baseName];
  if (mapped) {
    return mapped.includeArg && argument
      ? `${mapped.verb} ${argument}`.trim()
      : mapped.verb;
  }
  const fallbackVerb =
    baseName
      .replace(/^browser_/, '')
      .replace(/_/g, ' ')
      .trim() || baseName;
  return argument ? `${fallbackVerb} ${argument}`.trim() : fallbackVerb;
}

function safeToolInput(input: unknown): unknown {
  if (!isRecord(input)) return input;
  const safe: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (ACTIVITY_REDACT_KEYS.has(key)) continue;
    safe[key] =
      key === 'url' && typeof value === 'string'
        ? redactUrlForDisplay(value)
        : value;
  }
  return safe;
}

/**
 * Parse Claude stream-json NDJSON into ordered narration/action steps for the Trace panel.
 * Skips malformed lines, non-assistant events, empty text, and hidden plumbing tools.
 */
export function parseGenerationTraceNdjson(raw: string): GenerationTraceStep[] {
  const steps: GenerationTraceStep[] = [];
  for (const line of raw.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    let event: unknown;
    try {
      event = JSON.parse(trimmed);
    } catch {
      continue;
    }
    if (!isRecord(event) || event.type !== 'assistant') continue;
    const message = isRecord(event.message) ? event.message : null;
    const content = Array.isArray(message?.content) ? message.content : [];
    for (const block of content) {
      if (!isRecord(block)) continue;
      if (block.type === 'text' && typeof block.text === 'string') {
        const text = block.text.trim();
        if (text) steps.push({ kind: 'narration', text });
        continue;
      }
      if (block.type !== 'tool_use' || typeof block.name !== 'string') continue;
      if (HIDDEN_TOOLS.has(toolBaseName(block.name))) continue;
      const label = friendlyActionLabel(block.name, safeToolInput(block.input));
      if (label) steps.push({ kind: 'action', label });
    }
  }
  return steps;
}

/** Open only an indexed, identity-bound reference for the selected generation version. */
export function resolveGenerationTraceKey(
  _runId: string,
  _artifactKeys: string[] = [],
  context?: {
    schema_version?: number;
    projection?: string;
    versions?: Array<{
      version?: number;
      trace_key?: string;
      trace_ref?: import('@/lib/api/quality-types.js').GenerationContextVersion['trace_ref'];
    }>;
  } | null,
): string | null {
  if (context?.schema_version !== 2 || context.projection !== 'browser_v1')
    return null;
  const versions = context.versions ?? [];
  let latest = 0;
  for (const entry of versions) {
    if (
      typeof entry.version === 'number' &&
      Number.isSafeInteger(entry.version) &&
      entry.version > latest
    )
      latest = entry.version;
  }
  const selected = versions.filter((entry) => entry.version === latest);
  if (selected.length !== 1) return null;
  const ref = selected[0]?.trace_ref;
  if (ref?.state !== 'indexed' || !ref.identity || typeof ref.key !== 'string')
    return null;
  const identity = ref.identity;
  if (
    identity.generation_version !== latest ||
    !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(
      identity.owner_id,
    ) ||
    !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/.test(
      identity.case_execution_id,
    ) ||
    !Number.isSafeInteger(identity.attempt) ||
    identity.attempt < 0
  )
    return null;
  const prefix = `owners/${identity.owner_id}/cases/${identity.case_execution_id}/attempt-${identity.attempt}/artifacts/`;
  if (
    !ref.key.startsWith(prefix) ||
    !/^[a-f0-9]{64}\/trace\.jsonl$/.test(ref.key.slice(prefix.length))
  )
    return null;
  return ref.key;
}

/** View-model step for the Trace timeline (actions, notes, collapsed case JSON). */
export type TraceViewItem =
  | { kind: 'action'; label: string }
  | {
      kind: 'narration';
      text: string;
      collapsedByDefault: boolean;
      preview: string;
      lineCount: number;
    }
  | { kind: 'final_cases'; text: string; caseCount: number };

const NARRATION_COLLAPSE_CHARS = 280;
const NARRATION_COLLAPSE_LINES = 3;
const PREVIEW_CHARS = 72;

function looksLikeCaseObject(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    typeof value.title === 'string' ||
    typeof value.name === 'string' ||
    Array.isArray(value.steps) ||
    typeof value.expected === 'string' ||
    typeof value.expected_result === 'string' ||
    typeof value.preconditions === 'string'
  );
}

function parseCasesJsonArray(
  raw: string,
): { text: string; caseCount: number } | null {
  const trimmed = raw.trim();
  let candidate = trimmed;
  const fenced = /^```(?:json)?\s*\n([\s\S]*?)\n```$/i.exec(trimmed);
  if (fenced?.[1]) candidate = fenced[1].trim();
  if (!candidate.startsWith('[')) return null;
  try {
    const parsed: unknown = JSON.parse(candidate);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    if (!parsed.some(looksLikeCaseObject)) return null;
    return { text: JSON.stringify(parsed, null, 2), caseCount: parsed.length };
  } catch {
    return null;
  }
}

/**
 * Turn parsed NDJSON steps into a scannable Trace view model: actions stay one-line,
 * short narration stays open, long notes collapse, whole-step case JSON becomes Final cases.
 * When several narrations look like case JSON, only the last is peeled as Final cases.
 */
export function enrichTraceSteps(
  steps: GenerationTraceStep[],
): TraceViewItem[] {
  const caseIndexes: number[] = [];
  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index];
    if (step?.kind === 'narration' && parseCasesJsonArray(step.text)) {
      caseIndexes.push(index);
    }
  }
  const finalCasesIndex =
    caseIndexes.length > 0 ? caseIndexes[caseIndexes.length - 1] : -1;

  const items: TraceViewItem[] = [];
  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index];
    if (!step) continue;
    if (step.kind === 'action') {
      items.push({ kind: 'action', label: step.label });
      continue;
    }
    if (index === finalCasesIndex) {
      const parsed = parseCasesJsonArray(step.text);
      if (parsed) {
        items.push({
          kind: 'final_cases',
          text: parsed.text,
          caseCount: parsed.caseCount,
        });
        continue;
      }
    }
    const lines = step.text.split('\n');
    const lineCount = lines.length;
    const collapsedByDefault =
      step.text.length > NARRATION_COLLAPSE_CHARS ||
      lineCount > NARRATION_COLLAPSE_LINES;
    const firstLine = lines.find((line) => line.trim())?.trim() ?? step.text;
    const preview =
      firstLine.length > PREVIEW_CHARS
        ? `${firstLine.slice(0, PREVIEW_CHARS - 1)}…`
        : firstLine;
    items.push({
      kind: 'narration',
      text: step.text,
      collapsedByDefault,
      preview,
      lineCount,
    });
  }
  return items;
}
